import React, { useMemo } from 'react';
import './CreatorFilters.css';

function CreatorFilters({ filters = { gender: 'any', niche: 'any', age: 'any', hobbies: 'any' }, onFilterChange, creators = [] }) {
  const handleFilterChange = (filterType, value) => {
    if (onFilterChange) {
      onFilterChange({
        ...filters,
        [filterType]: value
      });
    }
  };

  // Extract unique niches from creators
  const availableNiches = useMemo(() => {
    const nicheSet = new Set();
    creators.forEach(creator => {
      const niches = creator.niches || creator.niche || [];
      const nicheArray = Array.isArray(niches) ? niches : (niches ? [niches] : []);
      nicheArray.forEach(niche => {
        if (niche && typeof niche === 'string') {
          nicheSet.add(niche.trim());
        }
      });
    });
    return Array.from(nicheSet).sort();
  }, [creators]);

  // Extract unique age ranges from creators
  const availableAgeRanges = useMemo(() => {
    const ageSet = new Set();
    creators.forEach(creator => {
      const age = creator.age || creator.ageRange;
      if (age) {
        if (typeof age === 'number') {
          // Convert single age to range
          if (age >= 18 && age <= 24) ageSet.add('18-24');
          else if (age >= 25 && age <= 34) ageSet.add('25-34');
          else if (age >= 35 && age <= 44) ageSet.add('35-44');
          else if (age >= 45 && age <= 54) ageSet.add('45-54');
          else if (age >= 55) ageSet.add('55+');
        } else if (typeof age === 'string' && age.includes('-')) {
          // Already a range like "18-24"
          ageSet.add(age);
        } else if (typeof age === 'string') {
          // Try to parse as number
          const ageNum = parseInt(age);
          if (!isNaN(ageNum)) {
            if (ageNum >= 18 && ageNum <= 24) ageSet.add('18-24');
            else if (ageNum >= 25 && ageNum <= 34) ageSet.add('25-34');
            else if (ageNum >= 35 && ageNum <= 44) ageSet.add('35-44');
            else if (ageNum >= 45 && ageNum <= 54) ageSet.add('45-54');
            else if (ageNum >= 55) ageSet.add('55+');
          }
        }
      }
    });
    // Sort age ranges logically
    const sortedAges = Array.from(ageSet).sort((a, b) => {
      const aMin = parseInt(a.split('-')[0] || a.replace('+', ''));
      const bMin = parseInt(b.split('-')[0] || b.replace('+', ''));
      return aMin - bMin;
    });
    return sortedAges;
  }, [creators]);

  // Extract unique hobbies from creators
  const availableHobbies = useMemo(() => {
    const hobbySet = new Set();
    creators.forEach(creator => {
      const hobbies = creator.hobbies || [];
      const hobbyArray = Array.isArray(hobbies) ? hobbies : (hobbies ? [hobbies] : []);
      hobbyArray.forEach(hobby => {
        if (hobby && typeof hobby === 'string') {
          hobbySet.add(hobby.trim());
        }
      });
    });
    return Array.from(hobbySet).sort();
  }, [creators]);

  return (
    <div className="creator-filters">
      <h4>Filter by:</h4>
      <label>
        Gender
        <select 
          value={filters.gender || 'any'} 
          onChange={(e) => handleFilterChange('gender', e.target.value)}
        >
          <option value="any">Any</option>
          <option value="female">Female</option>
          <option value="male">Male</option>
          <option value="other">Other</option>
        </select>
      </label>
      <label>
        Niche
        <select 
          value={filters.niche || 'any'} 
          onChange={(e) => handleFilterChange('niche', e.target.value)}
        >
          <option value="any">Any</option>
          {availableNiches.map(niche => (
            <option key={niche} value={niche.toLowerCase()}>
              {niche}
            </option>
          ))}
        </select>
      </label>
      <label>
        Age
        <select 
          value={filters.age || 'any'} 
          onChange={(e) => handleFilterChange('age', e.target.value)}
        >
          <option value="any">Any</option>
          {availableAgeRanges.map(ageRange => (
            <option key={ageRange} value={ageRange}>
              {ageRange}
            </option>
          ))}
        </select>
      </label>
      <label>
        Hobbies
        <select 
          value={filters.hobbies || 'any'} 
          onChange={(e) => handleFilterChange('hobbies', e.target.value)}
        >
          <option value="any">Any</option>
          {availableHobbies.map(hobby => (
            <option key={hobby} value={hobby.toLowerCase()}>
              {hobby}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

export default CreatorFilters;
